import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { Title } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { TABLE_SCROLL_SX } from '../../shared/tableScroll';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import {
  FEATURE_TRANSPOREON,
  TRANSPORT_FILTERS,
  ackLabel,
  downloadTransporeonExport,
  fetchTransporeonMessages,
  fetchTransporeonState,
  fetchTransporeonTransports,
  importTransporeonCopy,
  matchLabel,
  qualifierLabel,
  type TransporeonRawMessage,
  type TransporeonState,
  type TransporeonTransportItem,
} from './transporeonApi';

/**
 * Ekran „Transporeon” w sekcji administracyjnej (04.11.2026, przełącznik
 * `transporeonImport`). Raport pobrań z kolejki, transporty z dopasowaniem
 * do TMS (z filtrem „Brak w TMS”) i — tylko tu — surowe komunikaty.
 * Na stagingu i dev: import kopii komunikatów z produkcji; na produkcji:
 * eksport kopii. Nic stąd nie idzie do Transporeonu.
 */

export const FEATURE_OFF_NOTE =
  'Ekran „Transporeon” jest wyłączony w wersji aplikacji, z której korzystasz.';

export const INTRO_NOTE =
  'Integracja z Transporeonem to wyłącznie pobieranie: kolejkę czyta tylko produkcja, a pobranie potwierdza dopiero po zapisie w bazie. Staging i dev dostają kopię komunikatów zapisanych przez produkcję. Do Transporeonu nic nie wysyłamy — ani ofert, ani statusów.';

/** Wyłącznik modułu (Bartek 04.11.2026) — to samo zdanie co odmowa w API. */
export const PULL_ON_HOLD_NOTE = 'Pobieranie z Transporeonu wstrzymane do decyzji';

export const TransporeonScreen = () => {
  const on = useFeatureState(FEATURE_TRANSPOREON);
  if (on === undefined) return <FeaturesLoading />;
  if (!on) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('transporeon')} />
        <Alert severity="info">{FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <TransporeonScreenBody />;
};

const RawMessagesDialog = ({ transport, onClose }: { transport: TransporeonTransportItem; onClose: () => void }) => {
  const [rows, setRows] = useState<TransporeonRawMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    fetchTransporeonMessages(transport.id)
      .then(setRows)
      .catch((e) => setError(toErrorMessage(e)));
  }, [transport.id]);
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Surowe komunikaty — transport {transport.number}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Komunikaty bez maskowania (dane dyspozytorów zleceniodawcy) — tylko dla sekcji administracyjnej, nie kopiuj ich poza aplikację.
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
        {!rows && !error && <CircularProgress size={24} />}
        {rows?.map((m) => (
          <Box key={m.id} sx={{ mb: 2 }} data-testid="transporeon-raw-message">
            <Typography variant="subtitle2">
              {m.messageAtText ?? '—'} · {m.qualifier} ({qualifierLabel(m.qualifier)}) · wynik: {m.outcome ?? 'czeka'}
            </Typography>
            <Box
              component="pre"
              sx={{ fontSize: 12, maxHeight: 280, overflow: 'auto', bgcolor: 'action.hover', p: 1, m: 0 }}
            >
              {JSON.stringify(m.payload, null, 2)}
            </Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zamknij</Button>
      </DialogActions>
    </Dialog>
  );
};

const TransporeonScreenBody = () => {
  const [state, setState] = useState<TransporeonState | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [list, setList] = useState<{ total: number; items: TransporeonTransportItem[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [raw, setRaw] = useState<TransporeonTransportItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [since, setSince] = useState(() => new Date(Date.now() - 14 * 86_400_000).toISOString().slice(0, 10));

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([fetchTransporeonState(), fetchTransporeonTransports(filter)]);
      setState(s);
      setList(l);
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const onImport = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      await importTransporeonCopy(file);
      setNotice('Zaimportowano kopię komunikatów — transporty i alerty przeliczone.');
      await load();
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title={menuLabel('transporeon')} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {INTRO_NOTE}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {!state && !error && <CircularProgress size={24} />}
      {state && (
        <>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }} data-testid="transporeon-summary">
            {state.pullEnabled !== true && <Chip color="warning" label={PULL_ON_HOLD_NOTE} />}
            <Chip
              label={
                state.production
                  ? state.everySeconds
                    ? `Pobieranie co ${state.everySeconds} s`
                    : 'Pobieranie wyłączone'
                  : 'To nie produkcja — kolejki nie czytamy'
              }
            />
            <Chip label={state.acknowledge ? 'Potwierdzanie włączone' : 'Bez potwierdzania'} />
            <Chip label={`Komunikatów: ${state.messages}`} />
            {state.pendingMessages > 0 && <Chip color="warning" label={`Do przetworzenia: ${state.pendingMessages}`} />}
            <Chip color={state.missingInTms > 0 ? 'error' : 'default'} label={`Brak w TMS: ${state.missingInTms}`} />
          </Stack>
          {state.configErrors.map((e) => (
            <Alert key={e} severity="warning" sx={{ mb: 1 }}>
              {e}
            </Alert>
          ))}
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            {state.importAllowed ? (
              <Button variant="outlined" component="label" disabled={busy}>
                Importuj kopię komunikatów
                <input hidden type="file" accept="application/json,.json" onChange={(e) => void onImport(e)} />
              </Button>
            ) : (
              <>
                <TextField
                  size="small"
                  type="date"
                  label="Kopia od dnia"
                  value={since}
                  onChange={(e) => setSince(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <Button variant="outlined" onClick={() => void downloadTransporeonExport(since).catch((e) => setError(toErrorMessage(e)))}>
                  Pobierz kopię dla stagingu
                </Button>
              </>
            )}
          </Stack>

          <Typography variant="h6">Pobrania</Typography>
          <Box sx={{ ...TABLE_SCROLL_SX, mb: 3 }}>
            <Table size="small" aria-label="Pobrania">
              <TableHead>
                <TableRow>
                  <TableCell>Kiedy</TableCell>
                  <TableCell>Źródło</TableCell>
                  <TableCell>Komunikaty (nowe)</TableCell>
                  <TableCell>Alerty</TableCell>
                  <TableCell>Potwierdzenie</TableCell>
                  <TableCell>Błąd / uwagi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.pulls.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{dateTimeNoSeconds(p.startedAt)}</TableCell>
                    <TableCell>{p.origin === 'import' ? 'kopia' : 'kolejka'}</TableCell>
                    <TableCell>
                      {p.messageCount} ({p.newMessages})
                    </TableCell>
                    <TableCell>{p.alertsCreated}</TableCell>
                    <TableCell title={p.ackNote ?? undefined}>{ackLabel(p)}</TableCell>
                    <TableCell>{[p.error, ...p.notes].filter(Boolean).join('; ') || '—'}</TableCell>
                  </TableRow>
                ))}
                {state.pulls.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>Jeszcze nic nie pobrano.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </>
      )}

      <Typography variant="h6">Transporty</Typography>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', my: 1 }}>
        {TRANSPORT_FILTERS.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            color={filter === f.key ? 'primary' : 'default'}
            onClick={() => setFilter(f.key)}
            data-testid={`transporeon-filter-${f.key}`}
          />
        ))}
      </Stack>
      {list && (
        <Box sx={TABLE_SCROLL_SX}>
          <Table size="small" aria-label="Transporty z Transporeonu">
            <TableHead>
              <TableRow>
                <TableCell>Numer</TableCell>
                <TableCell>Zleceniodawca</TableCell>
                <TableCell>Trasa</TableCell>
                <TableCell>Ostatni komunikat</TableCell>
                <TableCell>TMS</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.items.map((t) => (
                <TableRow key={t.id} data-testid={`transporeon-row-${t.number}`}>
                  <TableCell>
                    {t.tpwLink ? (
                      <Link href={t.tpwLink} target="_blank" rel="noopener noreferrer">
                        {t.number}
                      </Link>
                    ) : (
                      t.number
                    )}
                  </TableCell>
                  <TableCell>{t.shipperName ?? '—'}</TableCell>
                  <TableCell>{t.route ?? '—'}</TableCell>
                  <TableCell>
                    {qualifierLabel(t.lastQualifier)} · {dateTimeNoSeconds(t.lastMessageAt)}
                  </TableCell>
                  <TableCell>
                    {t.missingInTms ? (
                      <Chip size="small" color="error" label="Przyjęte w Transporeonie, brak w TMS" />
                    ) : (
                      matchLabel(t.matchStatus)
                    )}
                    {t.matchedUserOfferIds.map((id) => (
                      <Link key={id} component={RouterLink} to={`/user-offers/${id}/show`} sx={{ ml: 1 }}>
                        zlecenie #{id}
                      </Link>
                    ))}
                    {t.preOrderId && (
                      <Link component={RouterLink} to={`/pre-orders/${t.preOrderId}/show`} sx={{ ml: 1 }}>
                        prezlecenie #{t.preOrderId}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => setRaw(t)}>
                      Surowe komunikaty
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {list.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>Brak transportów w tym widoku.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}
      {raw && <RawMessagesDialog transport={raw} onClose={() => setRaw(null)} />}
    </Box>
  );
};
